import React, { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useStores } from 'stores/hooks/hooks';
import { GrowerType, ResellerTreeNode, TreeGrower } from 'models/grower';
import { Paper } from '@mui/material';
import { Column } from 'components/shared/DataTable/DataTable';
import DataTable from 'components/shared/DataTable';
import { Select } from 'components/UI/Select/Select';
import Text from 'components/UI/Text';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import Checkbox from '@mui/material/Checkbox';
import Input from 'components/UI/Input';
import api from 'Api/ApiMethods';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 70vh;
`;

const SearchInput = styled(Input)``;

const ContentContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  flex: 1;
  overflow: hidden;
`;

const TableSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DestinationSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DataTableWrapper = styled(Paper)`
  flex: 1;
  overflow: hidden;
  background-color: var(--surface-card);

  .column-checkbox {
    width: 1rem;
    min-width: 1rem;
  }

  .p-datatable .p-datatable-tbody > tr > td {
    padding: 0.25rem 0.5rem;
  }

  .p-datatable .p-datatable-thead > tr > th {
    padding: 0.25rem 0.5rem !important;
  }

  .p-inputtext {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
`;

interface MoveDialogPickListProps {
  selection: TreeGrower[];
  destination: TreeGrower | null;
  onSelectionChange?: (selectedGrowers: TreeGrower[]) => void;
  onDestinationChange?: (destination: TreeGrower | null) => void;
}

const getRecursiveResellers = (
  resellers: ResellerTreeNode[],
): ResellerTreeNode[] => {
  const resellerList: ResellerTreeNode[] = [];

  resellers.forEach((reseller) => {
    if (reseller.growerType === GrowerType.Reseller) {
      resellerList.push(reseller);
    }

    if (reseller.growers && reseller.growers.length > 0) {
      resellerList.push(...getRecursiveResellers(reseller.growers));
    }
  });

  return resellerList;
};

const MoveDialogGrowerResellerSelect: FC<MoveDialogPickListProps> = observer(
  ({ onSelectionChange, onDestinationChange, selection, destination }) => {
    const { t } = useTranslation('grower');
    const { resellersStore } = useStores();
    const [filters] = useState({
      global: { value: '', matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<TreeGrower[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    console.log('destination', destination);
    console.log('selection', selection);

    const resellerArray = useMemo(() => {
      const allResellers = getRecursiveResellers(resellersStore.resellerTree);
      const seen = new Set<string>();
      return allResellers.filter((reseller) => {
        if (seen.has(reseller.uuid)) {
          return false;
        }

        seen.add(reseller.uuid);
        return true;
      });
    }, [resellersStore.resellerTree]);

    const resellerOptions = useMemo(
      () =>
        resellerArray.map((reseller) => ({
          label: reseller.name,
          value: reseller,
        })),
      [resellerArray],
    );

    const setTest = new Set(resellerArray.map((reseller) => reseller.id));
    console.log('setTest', setTest);

    const selectedRowIds = useMemo(
      () => new Set(selection.map((item) => item.id)),
      [selection],
    );

    const updateSelection = useCallback(
      (event: DataTableRowClickEvent) => {
        const { data } = event;
        let newSelection: TreeGrower[];

        if (selectedRowIds.has(data.id)) {
          newSelection = selection.filter((item) => item.id !== data.id);
        } else {
          newSelection = [...selection, data as TreeGrower];
        }

        onSelectionChange?.(newSelection);
      },
      [selection, selectedRowIds, onSelectionChange],
    );

    const getCheckboxBody = useCallback(
      (data: TreeGrower) => (
        <Checkbox
          checked={selectedRowIds.has(data.id)}
          onChange={() =>
            updateSelection({ data } as unknown as DataTableRowClickEvent)
          }
        />
      ),
      [updateSelection, selectedRowIds],
    );

    const tableValues = useMemo(
      () =>
        searchResults?.length
          ? searchResults
          : resellersStore.tableValues.filter(
            (grower) => grower.growerType === GrowerType.Grower,
          ),
      [resellersStore.tableValues, searchResults],
    );

    console.log('tableValues', JSON.parse(JSON.stringify(tableValues)));

    // Debounced search function
    const performSearchGrowers = useCallback(
      async (text: string) => {
        if (!text.trim()) {
          setSearchResults([]);
          setSearchError(null);
          return;
        }

        setIsSearching(true);
        setSearchError(null);

        try {
          const results = await api.getResellerSearchTree(text);

          // Extract growers from the search results
          const growers: TreeGrower[] = [];
          if (results && Array.isArray(results)) {
            growers.push(
              ...(results.filter(
                (grower) =>
                  grower.growerType === GrowerType.Grower &&
                  grower.resellerId ===
                  resellersStore.selectedTreeNode?.data?.id,
              ) || []),
            );
          }

          setSearchResults(growers);
        } catch (error) {
          setSearchError(t('errors:search_failed'));
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      },
      [resellersStore.selectedTreeNode?.data?.id, t],
    );

    console.log(
      'getRecursiveResellers',
      JSON.parse(
        JSON.stringify(getRecursiveResellers(resellersStore.resellerTree)),
      ),
    );

    // Debounce search input
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        performSearchGrowers(searchText);
      }, 500);

      return () => clearTimeout(timeoutId);
    }, [searchText, performSearchGrowers]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    };

    return (
      <Container>
        <ContentContainer>
          <TableSection>
            <Text variant="subtitle1" mb={0.5}>
              {t('grower_move_dialog_search_placeholder')}
            </Text>
            <SearchInput
              value={searchText}
              onChange={handleSearchChange}
              placeholder={t('grower_move_dialog_search_placeholder')}
              disabled={isSearching}
            />

            <Text variant="subtitle1" mb={0.5}>
              {t('grower_move_dialog_select_growers')}
            </Text>
            <DataTableWrapper elevation={1}>
              <DataTable
                id="MoveDialogItemsTable"
                showGridlines
                resizableColumns
                value={tableValues}
                scrollable
                scrollHeight="flex"
                selection={selection}
                dataKey="id"
                emptyMessage={t('empty_message')}
                stripedRows
                filters={filters}
                filterDisplay="row"
                globalFilterFields={['name']}
              >
                <Column
                  body={getCheckboxBody}
                  className="column-checkbox"
                  filterHeaderStyle={{ pointerEvents: 'none', opacity: 0 }}
                />
                <Column
                  field="name"
                  header={t('name')}
                  sortable
                  filter
                  filterPlaceholder={t('filter_by_name')}
                />
                <Column field="id" header={t('id')} sortable />
              </DataTable>
            </DataTableWrapper>
          </TableSection>

          <DestinationSection>
            <div>
              <Text variant="subtitle1" mb={0.5}>
                {t('grower_move_dialog_destination')}
              </Text>
              <Select
                value={destination}
                options={resellerOptions}
                onChange={(e) => {
                  onDestinationChange?.(e.value);
                }}
                placeholder={t('grower_move_dialog_select_destination')}
                optionLabel="label"
                filter
                showClear
              />
            </div>

            {destination && (
              <Paper elevation={1} style={{ padding: '0.5rem' }}>
                <Text variant="caption" mb={0.25}>
                  {t('reseller')}:
                </Text>
                <Text variant="body2">{destination.name}</Text>
              </Paper>
            )}

            {selection.length > 0 && (
              <Paper elevation={1} style={{ padding: '0.5rem' }}>
                <Text variant="body2">
                  {t('growers')}: {selection.length}
                </Text>
              </Paper>
            )}
          </DestinationSection>
        </ContentContainer>
      </Container>
    );
  },
);

export default MoveDialogGrowerResellerSelect;
